import { FlexBox } from "@joonapp/web-shared"
import { useMediaQuery } from "@mui/material"
import { Outlet, Route, Routes } from "react-router-dom"

import ModalProvider from "./components/modals/ModalProvider"
import { MobileSidebar } from "./components/nav/MobileSidebar"
import Navbar from "./components/nav/Navbar"
import { useAnalytics } from "./hooks/useAnalytics"
import { useUserQuery } from "./networking/queries"
import AuthPage from "./pages/auth"
import Onboarding from "./pages/onboarding"
import WeeklyProgress from "./pages/patientInfo/behaviorsIncidents/BehaviorsIncidentsPage"
import HomeRoutinesPage from "./pages/patientInfo/homeRoutines/HomeRoutinesPage"
import DataTasksTab from "./pages/patientInfo/parentTraining/dataTasks/DataTasksTab"
import ModulesTab from "./pages/patientInfo/parentTraining/modules/ModulesTab"
import ParentTrainingPage from "./pages/patientInfo/parentTraining/ParentTrainingPage"
import ParentConnect from "./pages/patientInfo/parentTraining/ParentTrainingsPage"
import TodoTasksTab from "./pages/patientInfo/parentTraining/todoTasks/TodoTasksTab"
import ParentTrainingSummary from "./pages/patientInfo/parentTraining/TrainingSummaryTab"
import PatientDetails from "./pages/patientInfo/patientDetails/PatientDetails"
import PatientInfo from "./pages/patientInfo/PatientInfo"
import PatientsDashboard from "./pages/patientsDashboard/PatientsDashboard"
import Resources from "./pages/resources/Resources"
import Security from "./pages/security"
import HIPPACompliance from "./pages/security/HIPPACompliance"
import PIIPolicy from "./pages/security/PIIPolicy"
import PrivacyPolicy from "./pages/security/PrivacyPolicy"
import Settings from "./pages/settings/Settings"
import AbaAssessmentTemplateGenerator from "./pages/tools/AbaAssessmentTemplateGenerator"
import BcbaGoalGenerator from "./pages/tools/BcbaGoalGenerator"
import BipTemplateGenerator from "./pages/tools/BipTemplateGenerator"
import DataCollectionHelper from "./pages/tools/DataCollectionHelper"
import GoalWritingAssistant from "./pages/tools/GoalWritingAssistant"
import InterventionStrategyGenerator from "./pages/tools/InterventionStrategyGenerator"
import ProgressNoteTemplateCreator from "./pages/tools/ProgressNoteTemplateCreator"
import SessionNoteSummarizer from "./pages/tools/SessionNoteSummarizer"
import TaskAnalysisBuilder from "./pages/tools/TaskAnalysisBuilder"
import ToolsIndex from "./pages/tools/ToolsIndex"
import { ParentTrainingTab, PatientInfoTab } from "./types"
import {
  useExperimentsQuery,
  useOnboardingExperiments,
} from "./util/experimentManager"

export const Routing = () => {
  const { user } = useUserQuery()
  useAnalytics()
  useExperimentsQuery()
  useOnboardingExperiments()

  const isLoggedIn = !!user
  const isMobile = useMediaQuery("(max-width: 800px)")

  const Layout = () => (
    <FlexBox
      direction="column"
      wrap={false}
      style={{
        height: "100vh",
        overflowX: "hidden",
        overflowY: isMobile ? "auto" : "hidden",
        width: "100vw",
      }}
    >
      {isLoggedIn && <Navbar />}
      <MobileSidebar />
      <FlexBox
        wrap={false}
        style={{ flexGrow: 1, overflow: isMobile ? "visible" : "auto" }}
      >
        <Outlet />
      </FlexBox>
      <ModalProvider />
    </FlexBox>
  )

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<PatientsDashboard />} />
        <Route path="/onboard" element={<Onboarding />} />
        <Route path="/auth/:type" element={<AuthPage />} />

        {/* Patient Info */}
        <Route path="/patients" element={<PatientsDashboard />} />
        <Route path="/patients/:userId" element={<PatientInfo />}>
          <Route path={PatientInfoTab.BEHAVIORS} element={<WeeklyProgress />} />
          <Route
            path={PatientInfoTab.PARENT_TRAINING}
            element={<ParentConnect />}
          />

          <Route
            path={PatientInfoTab.PATIENT_DETAILS}
            element={<PatientDetails />}
          />
          <Route
            path={PatientInfoTab.HOME_ROUTINES}
            element={<HomeRoutinesPage />}
          />
          <Route
            path={`/patients/:userId/${PatientInfoTab.PARENT_TRAINING}/:trainingId`}
            element={<ParentTrainingPage />}
          >
            <Route
              path={ParentTrainingTab.SUMMARY}
              element={<ParentTrainingSummary />}
            />
            <Route path={ParentTrainingTab.MODULES} element={<ModulesTab />} />
            <Route
              path={ParentTrainingTab.DATA_TASKS}
              element={<DataTasksTab />}
            />
            <Route
              path={ParentTrainingTab.TODO_TASKS}
              element={<TodoTasksTab />}
            />
          </Route>
        </Route>

        <Route path="/resources" element={<Resources />} />

        {/* Tools */}
        <Route path="/tools" element={<ToolsIndex />} />
        <Route
          path="/tools/bcba-goal-generator"
          element={<BcbaGoalGenerator />}
        />
        <Route
          path="/tools/goal-writing-assistant"
          element={<GoalWritingAssistant />}
        />
        <Route
          path="/tools/task-analysis-builder"
          element={<TaskAnalysisBuilder />}
        />
        <Route
          path="/tools/intervention-strategy-generator"
          element={<InterventionStrategyGenerator />}
        />
        <Route
          path="/tools/bip-template-generator"
          element={<BipTemplateGenerator />}
        />
        <Route
          path="/tools/progress-note-template-creator"
          element={<ProgressNoteTemplateCreator />}
        />
        <Route
          path="/tools/session-note-summarizer"
          element={<SessionNoteSummarizer />}
        />
        <Route
          path="/tools/aba-assessment-template-generator"
          element={<AbaAssessmentTemplateGenerator />}
        />
        <Route
          path="/tools/data-collection-helper"
          element={<DataCollectionHelper />}
        />

        {/* Security & Privacy */}
        <Route path="/security" element={<Security />} />
        <Route path="/security/hipaa" element={<HIPPACompliance />} />
        <Route path="/security/privacy" element={<PrivacyPolicy />} />
        <Route path="/security/pii" element={<PIIPolicy />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
    </Routes>
  )
}
